import {
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
  Grid,
  InputBase,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openCompany, setOpenCompany] = useState(false);
  const [openServices, setOpenServices] = useState(false);
  const [openContact, setOpenContact] = useState(false);

  const services = [
    "Graphic Designing",
    "Software Development",
    "Game Development",
    "Generative AI",
    "Content Marketing",
  ];

  const companyInfos = [
    {
      title: "About Us",
      address: "/about-us",
    },
    {
      title: "Blog",
      address: "/blog",
    },
    {
      title: "Careers",
      address: "/career",
    },
    {
      title: "Features",
      address: "/features",
    },
  ];

  const contactInfo = [
    "+ 92 1234 567890",
    "abc123@gmail.com",
    "John Doe, 456 Elm Street, Suite 3, Los Angeles, CA 90001, USA.",
  ];

  const socialIcons = [
    { Icon: GoogleIcon },
    { Icon: FacebookIcon },
    { Icon: YouTubeIcon },
    { Icon: PlayArrowIcon },
    { Icon: TwitterIcon },
    { Icon: InstagramIcon },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        color: "white",
        backgroundColor: "#000000",
        backgroundImage: `url('/images/footerbackground.png')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
        borderTopLeftRadius: { xs: "30px", md: "60px" },
        borderTopRightRadius: { xs: "30px", md: "60px" },
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "400",
            mb: 4,
            fontFamily: "'Jaro', sans-serif",
            letterSpacing: "1px",
            textAlign: "center",
            color: "white",
            fontSize: { xs: "2.5rem", md: "3rem" },
          }}
        >
          Coding Forge
        </Typography>

        {/* Main content */}
        <Grid container spacing={4} justifyContent="center">
          {/* Left Column - Newsletter */}
          <Grid
            item
            xs={12}
            md={3.5}
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: "rgb(255, 255, 255)",
                  fontSize: "20px",
                  fontWeight: "500",
                  lineHeight: "32px",
                  fontFamily: "'Jaro', sans-serif",
                }}
              >
                Join Our Newsletter
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: "rgb(255, 255, 255)",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                }}
              >
                We'll send you a nice letter once per week. No spam.
              </Typography>

              {/* Subscribe Input */}
              <Box
                sx={{
                  display: "flex",
                  border: "2px solid white",
                  borderRadius: "50px",
                  overflow: "hidden",
                  maxWidth: isMobile ? "90%" : "100%",
                  mx: isMobile ? "auto" : 0,
                  mb: 4,
                }}
              >
                <InputBase
                  placeholder="Enter your email"
                  sx={{
                    ml: 2,
                    flex: 1,
                    fontSize: "14px",
                    color: "white",
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                    fontWeight: "400",
                    "&::placeholder": {
                      color: "rgba(255, 255, 255, 0.7)",
                      opacity: 1,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    m: 0.5,
                    borderRadius: "50px",
                    bgcolor: "white",
                    color: "black",
                    "&:hover": {
                      bgcolor: "#f5f5f5",
                    },
                    textTransform: "none",
                    fontWeight: "bold",
                    px: 3,
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                    fontSize: "14px",
                  }}
                >
                  Subscribe
                </Button>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  mb: 2,
                  color: "rgb(255, 255, 255)",
                  fontSize: "20px",
                  fontWeight: "500",
                  lineHeight: "32px",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                }}
              >
                FOLLOW US
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: isMobile ? "center" : "flex-start",
                }}
              >
                {socialIcons.map(({ Icon }, index) => (
                  <IconButton
                    key={index}
                    sx={{
                      color: "white",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    <Icon />
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Middle and Right Columns */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {/* Services Column */}
              <Grid
                item
                xs={12}
                sm={4}
                component={motion.div}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                  <Typography
                    variant="h6"
                    onClick={() => isMobile && setOpenServices(!openServices)}
                    sx={{
                      mb: 2,
                      color: "rgb(255, 255, 255)",
                      fontSize: "20px",
                      fontWeight: "500",
                      lineHeight: "32px",
                      fontFamily: "'Jaro', sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isMobile ? "center" : "flex-start",
                      cursor: isMobile ? "pointer" : "default",
                    }}
                  >
                    Services
                  </Typography>

                  {/* Desktop links */}
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    {services.map((service, index) => (
                      <Typography
                        key={index}
                        component="div"
                        sx={{
                          mb: 1,
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          "&:hover": { color: "white" },
                          cursor: "pointer",
                        }}
                      >
                        {service}
                      </Typography>
                    ))}
                  </Box>

                  {/* Mobile collapsible */}
                  <Collapse in={isMobile && openServices}>
                    {services.map((service, index) => (
                      <Typography
                        key={index}
                        component="div"
                        sx={{
                          mb: 1,
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          "&:hover": { color: "white" },
                          cursor: "pointer",
                        }}
                      >
                        {service}
                      </Typography>
                    ))}
                  </Collapse>
                </Box>
              </Grid>

              {/* Company Column */}
              <Grid
                item
                xs={12}
                sm={4}
                component={motion.div}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                  <Typography
                    variant="h6"
                    onClick={() => isMobile && setOpenCompany(!openCompany)}
                    sx={{
                      mb: 2,
                      color: "rgb(255, 255, 255)",
                      fontSize: "20px",
                      fontWeight: "500",
                      lineHeight: "32px",
                      fontFamily: "'Jaro', sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isMobile ? "center" : "flex-start",
                      cursor: isMobile ? "pointer" : "default",
                    }}
                  >
                    Company
                  </Typography>

                  {/* Desktop links */}
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    {companyInfos.map((info, index) => (
                      <Typography
                        key={index}
                        component={NavLink}
                        to={info.address}
                        sx={{
                          mb: 1,
                          display: "block",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          textDecoration: "none",
                          "&:hover": { color: "white" },
                          cursor: "pointer",
                        }}
                      >
                        {info.title}
                      </Typography>
                    ))}
                  </Box>

                  {/* Mobile collapsible */}
                  <Collapse in={isMobile && openCompany}>
                    {companyInfos.map((info, index) => (
                      <Typography
                        key={index}
                        component={NavLink}
                        to={info.address}
                        sx={{
                          mb: 1,
                          display: "block",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          textDecoration: "none",
                          "&:hover": { color: "white" },
                          cursor: "pointer",
                        }}
                      >
                        {info.title}
                      </Typography>
                    ))}
                  </Collapse>
                </Box>
              </Grid>

              {/* Contact Column */}
              <Grid
                item
                xs={12}
                sm={4}
                component={motion.div}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                  <Typography
                    variant="h6"
                    onClick={() => isMobile && setOpenContact(!openContact)}
                    sx={{
                      mb: 2,
                      color: "rgb(255, 255, 255)",
                      fontSize: "20px",
                      fontWeight: "500",
                      lineHeight: "32px",
                      fontFamily: "'Jaro', sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isMobile ? "center" : "flex-start",
                      cursor: isMobile ? "pointer" : "default",
                    }}
                  >
                    Contact
                  </Typography>

                  {/* Desktop links */}
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    {contactInfo.map((contact, index) => (
                      <Typography
                        key={index}
                        component="div"
                        sx={{
                          mb: 1,
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          "&:hover": { color: "white" },
                          cursor: "pointer",
                        }}
                      >
                        {contact}
                      </Typography>
                    ))}
                  </Box>

                  {/* Mobile collapsible */}
                  <Collapse in={isMobile && openContact}>
                    {contactInfo.map((contact, index) => (
                      <Typography
                        key={index}
                        component="div"
                        sx={{
                          mb: 1,
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          "&:hover": { color: "white" },
                          cursor: "pointer",
                        }}
                      >
                        {contact}
                      </Typography>
                    ))}
                  </Collapse>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Book A Meeting Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 5,
          }}
        >
          <Button
            component={motion.button}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            variant="contained"
            sx={{
              backgroundColor: "white",
              color: "black",
              borderRadius: "40px",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "14px",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              px: 4,
              py: 1,
              minWidth: "200px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Book A Meeting
          </Button>
        </Box>

        {/* Copyright */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: "400",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              mb: { xs: 1, sm: 0 },
            }}
          >
            © 2025 Coding Forge. All Rights Reserved.
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: "400",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            Terms & Conditions | Privacy Policy
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
